import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  CalendarCheck,
  ChatText,
  Phone,
  Storefront,
  Trash,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AppHeader } from "@/components/AppHeader";
import { PulledToggle } from "@/components/UpcomingViewings";
import { api, formatApiErrorDetail } from "@/lib/api";
import { loadCompany } from "@/lib/company";
import { smsHref, viewingText } from "@/lib/sms";

const STATUSES = ["Booked", "Done", "No-show", "Cancelled"];
const STATUS_TONE = {
  Booked: "border-cyan-400/40 bg-cyan-500/10 text-cyan-200",
  Done: "border-emerald-400/40 bg-emerald-500/10 text-emerald-200",
  "No-show": "border-amber-400/40 bg-amber-500/10 text-amber-200",
  Cancelled: "border-zinc-700 text-zinc-500",
};

// Staff view of one showroom viewing booked from the "Arrange a viewing" alert.
export default function ViewingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [form, setForm] = useState(null);
  const [shop, setShop] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/viewings/${id}`);
    setV(data);
    setForm({ date: data.date, time: data.time, assignee: data.assignee, notes: data.notes, status: data.status });
  }, [id]);

  useEffect(() => {
    load().catch(() => toast.error("Viewing not found"));
    loadCompany().then((c) => setShop(c?.name || ""));
  }, [load]);

  const save = async (patch = {}) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/viewings/${id}`, { ...form, ...patch });
      setV(data);
      setForm({ date: data.date, time: data.time, assignee: data.assignee, notes: data.notes, status: data.status });
      toast.success("Viewing updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    }
    setBusy(false);
  };

  const remove = async () => {
    try {
      await api.delete(`/viewings/${id}`);
      toast.success("Viewing removed");
      navigate("/calendar");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove");
    }
  };

  if (!v || !form) {
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Calendar / Viewing" />
        <main className="relative z-10 pt-6">
          <div className="h-40 animate-pulse rounded-lg bg-zinc-900" />
        </main>
      </div>
    );
  }

  const first = (v.customer_name || "").split(/\s+/)[0];
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const input = "h-11 border-zinc-800 bg-zinc-950";

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Calendar / Viewing" />
      <main className="relative z-10 pb-28 pt-6">
        <Link
          to="/calendar"
          data-testid="back-to-calendar"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> Calendar
        </Link>

        <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid="viewing-header-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500">
                <Storefront size={14} weight="bold" className="text-cyan-300" /> Showroom viewing
              </div>
              <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl" data-testid="viewing-customer">
                {v.customer_name}
              </h1>
              <p className="mt-1 text-sm text-zinc-400" data-testid="viewing-item">
                wants to see <span className="text-zinc-200">{v.item || "a slab"}</span>
                {v.where ? ` at ${v.where}` : ""}
              </p>
              <span
                data-testid="viewing-status"
                className={`mt-3 inline-flex rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${
                  STATUS_TONE[v.status] || STATUS_TONE.Booked
                }`}
              >
                {v.status}
              </span>
              <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="viewing-pulled-block">
                <PulledToggle v={v} onChange={setV} size="lg" />
                <span className="text-xs text-zinc-400" data-testid="viewing-pulled-meta">
                  {v.pulled
                    ? `On the rack since ${v.pulled_at?.slice(0, 16).replace("T", " ") || "—"}${
                        v.pulled_by ? ` · ${v.pulled_by}` : ""
                      }`
                    : "Tick once the slab is pulled and waiting on the rack"}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {v.phone && (
                <a
                  href={smsHref(v.phone, viewingText(v.customer_name, shop, v.item, v.date, v.time, v.where))}
                  data-testid="viewing-text-customer"
                  className="inline-flex h-11 items-center gap-2 rounded-md bg-indigo-500 px-4 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                >
                  <ChatText size={15} weight="bold" /> Text {first}
                </a>
              )}
              {v.phone && (
                <a
                  href={`tel:${v.phone.replace(/[^\d+]/g, "")}`}
                  data-testid="viewing-call-customer"
                  className="inline-flex h-11 items-center gap-2 rounded-md border border-zinc-700 px-4 text-xs uppercase tracking-widest hover:bg-zinc-800"
                >
                  <Phone size={15} weight="bold" /> {v.phone}
                </a>
              )}
              <Button
                data-testid="delete-viewing-button"
                variant="outline"
                onClick={remove}
                className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest text-red-400 hover:bg-zinc-800"
              >
                <Trash size={15} weight="bold" />
              </Button>
            </div>
          </div>
          {v.message && (
            <p className="mt-4 whitespace-pre-wrap border-t border-zinc-800 pt-4 text-sm text-zinc-300" data-testid="viewing-message">
              “{v.message}”
            </p>
          )}
          {v.slab_key && (
            <a
              href={`/inventory/${v.slab_key}`}
              target="_blank"
              rel="noreferrer"
              data-testid="viewing-slab-link"
              className="mt-3 inline-block text-xs uppercase tracking-widest text-yellow-400 hover:underline"
            >
              Open the public slab page →
            </a>
          )}
        </div>

        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4" data-testid="viewing-schedule-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500">
            <CalendarCheck size={14} weight="bold" className="text-cyan-300" /> When &amp; who
          </div>
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Date</Label>
              <Input data-testid="viewing-date-input" type="date" value={form.date} onChange={set("date")} className={input} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Time</Label>
              <Input data-testid="viewing-time-input" type="time" value={form.time} onChange={set("time")} className={input} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Who shows them</Label>
              <Input
                data-testid="viewing-assignee-input"
                value={form.assignee}
                onChange={set("assignee")}
                placeholder="Anyone at the desk"
                className={input}
              />
            </div>
            <div className="space-y-2 sm:col-span-3">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Notes</Label>
              <Textarea data-testid="viewing-notes-input" rows={2} value={form.notes} onChange={set("notes")} className="border-zinc-800 bg-zinc-950" />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button
              data-testid="save-viewing-button"
              disabled={busy}
              onClick={() => save()}
              className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              {busy ? "Saving…" : "Save"}
            </Button>
            <span className="ml-2 text-[10px] uppercase tracking-widest text-zinc-500">Mark</span>
            {STATUSES.filter((s) => s !== v.status).map((s) => (
              <Button
                key={s}
                data-testid={`viewing-status-${s.toLowerCase()}`}
                variant="outline"
                disabled={busy}
                onClick={() => save({ status: s })}
                className="h-9 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
              >
                {s}
              </Button>
            ))}
          </div>
          <p className="mt-3 text-xs text-zinc-500" data-testid="viewing-meta">
            Booked {v.created_at?.slice(0, 10)} via {v.booked_by || "office"}
          </p>
        </div>
      </main>
    </div>
  );
}
